/**
 * Shader compile / link with logs, uniform tables and typed uniform setters (W2-01a preview-gl; A§7.3).
 *
 * Programs report failures as data (stage + info log) instead of throwing: a custom shader from a chart package
 * that does not compile is a chart problem (PH-33, RenderStats.shaderErrors), not a renderer crash. Attribute
 * locations are bound before linking so every program shares one vertex layout.
 */

import { glObject } from './glState';

/** One active uniform: location, GL type (0 when the driver gave none) and array size. */
export interface UniformInfo {
  readonly name: string;
  readonly loc: WebGLUniformLocation;
  readonly type: number;
  readonly size: number;
}

export interface LinkedProgram {
  readonly program: WebGLProgram;
  /** Active uniforms by name (array uniforms also under their base name). */
  readonly uniforms: ReadonlyMap<string, UniformInfo>;
  /** Location of a uniform by name (looked up and cached on first use; null when inactive). */
  uniform(name: string): WebGLUniformLocation | null;
}

export type ShaderStage = 'vertex' | 'fragment' | 'link';

export type BuildResult =
  | { readonly ok: true; readonly prog: LinkedProgram }
  | { readonly ok: false; readonly stage: ShaderStage; readonly log: string };

function compile(
  gl: WebGL2RenderingContext,
  type: number,
  src: string,
): { readonly shader: WebGLShader } | { readonly log: string } {
  const shader = gl.createShader(type);
  if (shader === null) return { log: 'createShader failed (context lost?)' };
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  const ok: unknown = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
  if (ok !== true) {
    const log = (gl.getShaderInfoLog(shader) ?? '').trim();
    gl.deleteShader(shader);
    return { log: log !== '' ? log : ok === null ? 'context lost' : 'compile failed' };
  }
  return { shader };
}

function uniformTable(gl: WebGL2RenderingContext, program: WebGLProgram): Map<string, UniformInfo> {
  const out = new Map<string, UniformInfo>();
  const n: unknown = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
  const count = typeof n === 'number' ? n : 0;
  for (let i = 0; i < count; i++) {
    const info = gl.getActiveUniform(program, i) as WebGLActiveInfo | null | undefined;
    if (info === null || info === undefined) continue;
    const loc = gl.getUniformLocation(program, info.name);
    if (loc === null) continue;
    const u: UniformInfo = { name: info.name, loc, type: info.type, size: info.size };
    out.set(info.name, u);
    if (info.name.endsWith('[0]')) out.set(info.name.slice(0, -3), u);
  }
  return out;
}

/** Compile and link a program; attribute locations from `attribs` are bound before linking. */
export function buildProgram(
  gl: WebGL2RenderingContext,
  vertexSrc: string,
  fragmentSrc: string,
  attribs: Readonly<Record<string, number>>,
): BuildResult {
  const vs = compile(gl, gl.VERTEX_SHADER, vertexSrc);
  if (!('shader' in vs)) return { ok: false, stage: 'vertex', log: vs.log };
  const fs = compile(gl, gl.FRAGMENT_SHADER, fragmentSrc);
  if (!('shader' in fs)) {
    gl.deleteShader(vs.shader);
    return { ok: false, stage: 'fragment', log: fs.log };
  }
  const program = glObject(gl.createProgram());
  if (program === null) {
    gl.deleteShader(vs.shader);
    gl.deleteShader(fs.shader);
    return { ok: false, stage: 'link', log: 'createProgram failed (context lost?)' };
  }
  gl.attachShader(program, vs.shader);
  gl.attachShader(program, fs.shader);
  for (const [name, loc] of Object.entries(attribs)) gl.bindAttribLocation(program, loc, name);
  gl.linkProgram(program);
  gl.detachShader(program, vs.shader);
  gl.detachShader(program, fs.shader);
  gl.deleteShader(vs.shader);
  gl.deleteShader(fs.shader);
  const linked: unknown = gl.getProgramParameter(program, gl.LINK_STATUS);
  if (linked !== true) {
    const log = (gl.getProgramInfoLog(program) ?? '').trim();
    gl.deleteProgram(program);
    return { ok: false, stage: 'link', log: log === '' ? 'link failed' : log };
  }
  const uniforms = uniformTable(gl, program);
  const locs = new Map<string, WebGLUniformLocation | null>();
  const prog: LinkedProgram = {
    program,
    uniforms,
    uniform(name) {
      const known = uniforms.get(name);
      if (known !== undefined) return known.loc;
      if (locs.has(name)) return locs.get(name) ?? null;
      const loc = gl.getUniformLocation(program, name);
      locs.set(name, loc);
      return loc;
    },
  };
  return { ok: true, prog };
}

/** Same as `buildProgram` for our own shaders: a failure is a bug, so it throws with the log. */
export function buildProgramOrThrow(
  gl: WebGL2RenderingContext,
  vertexSrc: string,
  fragmentSrc: string,
  attribs: Readonly<Record<string, number>>,
  label: string,
): LinkedProgram {
  const r = buildProgram(gl, vertexSrc, fragmentSrc, attribs);
  if (!r.ok) throw new Error(`preview-gl: internal shader '${label}' failed (${r.stage}): ${r.log}`);
  return r.prog;
}

/** Number of float components of a GL uniform type (0 = not a float type handled here). */
export function floatComponents(gl: WebGL2RenderingContext, type: number): number {
  switch (type) {
    case gl.FLOAT:
      return 1;
    case gl.FLOAT_VEC2:
      return 2;
    case gl.FLOAT_VEC3:
      return 3;
    case gl.FLOAT_VEC4:
      return 4;
    default:
      return 0;
  }
}

/** Number of int components of a GL uniform type (0 = not an int / bool type). */
export function intComponents(gl: WebGL2RenderingContext, type: number): number {
  switch (type) {
    case gl.INT:
    case gl.BOOL:
      return 1;
    case gl.INT_VEC2:
    case gl.BOOL_VEC2:
      return 2;
    case gl.INT_VEC3:
    case gl.BOOL_VEC3:
      return 3;
    case gl.INT_VEC4:
    case gl.BOOL_VEC4:
      return 4;
    default:
      return 0;
  }
}

const scratch = new Float32Array(4);
const scratchI = new Int32Array(4);

/** Component `k` of a uniform value; missing components (and NaN) read as 0. */
function component(value: number | readonly number[], k: number): number {
  const v = typeof value === 'number' ? (k === 0 ? value : 0) : (value[k] ?? 0);
  return Number.isFinite(v) ? v : 0;
}

/**
 * Sets a uniform from a number or vector, converted to the uniform's declared GLSL type: missing components are 0,
 * extra ones are ignored, int / bool uniforms get rounded values. Without a known type (driver gave none), the
 * value's own shape decides (1 -> float, 2 -> vec2, 3 -> vec3, 4 -> vec4).
 */
export function setUniformValue(
  gl: WebGL2RenderingContext,
  loc: WebGLUniformLocation,
  type: number,
  value: number | readonly number[],
): void {
  let fc = floatComponents(gl, type);
  const ic = fc === 0 ? intComponents(gl, type) : 0;
  if (fc === 0 && ic === 0) {
    if (type !== 0) return;
    fc = typeof value === 'number' ? 1 : Math.max(1, Math.min(4, value.length));
  }
  if (fc > 0) {
    for (let k = 0; k < fc; k++) scratch[k] = component(value, k);
    if (fc === 1) gl.uniform1f(loc, scratch[0] ?? 0);
    else if (fc === 2) gl.uniform2f(loc, scratch[0] ?? 0, scratch[1] ?? 0);
    else if (fc === 3) gl.uniform3f(loc, scratch[0] ?? 0, scratch[1] ?? 0, scratch[2] ?? 0);
    else gl.uniform4f(loc, scratch[0] ?? 0, scratch[1] ?? 0, scratch[2] ?? 0, scratch[3] ?? 0);
    return;
  }
  for (let k = 0; k < ic; k++) scratchI[k] = Math.round(component(value, k));
  if (ic === 1) gl.uniform1i(loc, scratchI[0] ?? 0);
  else if (ic === 2) gl.uniform2i(loc, scratchI[0] ?? 0, scratchI[1] ?? 0);
  else if (ic === 3) gl.uniform3i(loc, scratchI[0] ?? 0, scratchI[1] ?? 0, scratchI[2] ?? 0);
  else gl.uniform4i(loc, scratchI[0] ?? 0, scratchI[1] ?? 0, scratchI[2] ?? 0, scratchI[3] ?? 0);
}

/** Sets a uniform by name when the program has it (inactive / unknown names are ignored, like Phira). */
export function setNamedUniform(
  gl: WebGL2RenderingContext,
  prog: LinkedProgram,
  name: string,
  value: number | readonly number[],
): void {
  const info = prog.uniforms.get(name);
  if (info !== undefined) {
    setUniformValue(gl, info.loc, info.type, value);
    return;
  }
  const loc = prog.uniform(name);
  if (loc !== null) setUniformValue(gl, loc, 0, value);
}
